/**
 * Distance cadence for Holly's bell, deliberately before either audio or stealth.
 *
 * Companion simulation owns travelled distance. This adapter only turns that
 * already-deterministic trace into precise positions a later presentation/audio
 * and sound-channel integration can consume without creating another clock.
 */

import type { CompanionSnapshot, Vec2 } from '@/systems/companion';

const TRACE_EPSILON = 0.000000001;

export interface CompanionBellConfig {
  /** Metres between bell events. Authored by the room, never a global default. */
  readonly strideDistance: number;
  /** The later stealth integration's sound radius, carried without interpreting it. */
  readonly soundRadius: number;
}

export interface CompanionBellEvent {
  readonly position: Vec2;
  readonly radius: number;
  /** Total companion travel at this exact cadence mark. */
  readonly thresholdDistance: number;
}

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`companion bell ${label} must be finite`);
}

function positive(value: number, label: string): void {
  finite(value, label);
  if (value <= 0) throw new Error(`companion bell ${label} must be positive`);
}

function validateSnapshot(snapshot: CompanionSnapshot, label: string): void {
  finite(snapshot.x, `${label}.x`);
  finite(snapshot.z, `${label}.z`);
  finite(snapshot.travelledDistance, `${label}.travelledDistance`);
  if (snapshot.travelledDistance < 0) {
    throw new Error(`companion bell ${label}.travelledDistance must not be negative`);
  }
}

/**
 * A relative tolerance, and it carries an assumption worth stating.
 *
 * It scales with the magnitude of the distances being compared, which is right
 * for float arithmetic and wrong at absurd magnitudes: at a travelled distance
 * around 1e12 the tolerance grows wide enough to admit a threshold that was not
 * actually crossed. That needs Holly to walk a billion kilometres without a
 * reset, and `travelledDistance` is restored to its authored value by the normal
 * beat reset, so the bound is a scene, not a play session. An independent review
 * raised it; it is recorded rather than defended against, because the defence
 * (an absolute epsilon) would be wrong at the scale the game actually runs at.
 */
function tolerance(left: number, right: number): number {
  return TRACE_EPSILON * Math.max(1, Math.abs(left), Math.abs(right));
}

/**
 * Pure, fixed-tick bell cadence. The snapshots' phase and `moving` flag are not
 * inputs to cadence: only actual travelled distance can make Holly ring.
 */
export class CompanionBell {
  readonly #strideDistance: number;
  readonly #soundRadius: number;
  #lastDistance: number;
  #nextThreshold: number;

  constructor(config: CompanionBellConfig, initial: CompanionSnapshot) {
    positive(config.strideDistance, 'strideDistance');
    positive(config.soundRadius, 'soundRadius');
    validateSnapshot(initial, 'initial');
    this.#strideDistance = config.strideDistance;
    this.#soundRadius = config.soundRadius;
    this.#lastDistance = initial.travelledDistance;
    this.#nextThreshold = initial.travelledDistance + config.strideDistance;
  }

  /** Capture/reset begins a new cadence from that exact restored distance. */
  reset(snapshot: CompanionSnapshot): void {
    validateSnapshot(snapshot, 'reset');
    this.#lastDistance = snapshot.travelledDistance;
    this.#nextThreshold = snapshot.travelledDistance + this.#strideDistance;
  }

  /**
   * Consume exactly one pair of consecutive fixed snapshots.
   *
   * **The displacement check is one-sided, and the asymmetry is the point.**
   * `Companion.#resolveObstacles` and `Companion.#projectOutside` both write
   * Holly's position without incrementing `travelledDistance` — deliberately, and
   * their own headers say so. `#resolveObstacles` runs BEFORE `#moveToward` in
   * the same tick, so the first time a tipping pallet's footprint arrives under
   * her she is pushed clear and then walks, and the endpoints are farther apart
   * than the metres she covered. A two-sided check threw out of `fixedUpdate`
   * there. Displacement in excess of travel is a constraint being satisfied;
   * travel in excess of displacement is a caller inventing ground she did not
   * cross, and that is still rejected.
   *
   * **A tick that projected does not get interpolated.** The chord from
   * `previous` to `current` is not a path she walked when part of it was a
   * teleport, and interpolating along it can put the stealth source metres off
   * her — an independent review's finding, and the reason this is not simply
   * documented as a small error. Such a tick rings at `current` instead: at
   * Holly's own move speed that is at most one fixed step of travel from the
   * true crossing point, about 19 mm, rather than the width of whatever she was
   * pushed out of.
   *
   * Every check happens before state is accepted, so a malformed caller cannot
   * shift cadence and make an otherwise valid later trace ring at the wrong mark.
   */
  advance(previous: CompanionSnapshot, current: CompanionSnapshot): readonly CompanionBellEvent[] {
    validateSnapshot(previous, 'previous');
    validateSnapshot(current, 'current');
    if (previous.travelledDistance !== this.#lastDistance) {
      throw new Error('companion bell previous travelledDistance does not match accepted trace');
    }
    if (current.travelledDistance < previous.travelledDistance) {
      throw new Error('companion bell travelledDistance must not decrease');
    }

    const distanceDelta = current.travelledDistance - previous.travelledDistance;
    const dx = current.x - previous.x;
    const dz = current.z - previous.z;
    const positionalDelta = Math.hypot(dx, dz);
    if (distanceDelta > positionalDelta + tolerance(positionalDelta, distanceDelta)) {
      throw new Error('companion bell travelledDistance must match positional displacement');
    }
    // Displacement she did not walk means the segment is not a path. See above.
    const projected = positionalDelta > distanceDelta + tolerance(positionalDelta, distanceDelta);

    const events: CompanionBellEvent[] = [];
    if (distanceDelta > 0) {
      while (this.#nextThreshold <= current.travelledDistance + tolerance(this.#nextThreshold, current.travelledDistance)) {
        // Repeated decimal strides (0.1 is the common case) may land just above
        // a represented endpoint. Snap that nearly-equal cadence mark to the
        // accepted distance now, rather than delaying it into the next tick and
        // bunching two bells into one segment.
        const thresholdDistance = Math.abs(this.#nextThreshold - current.travelledDistance)
          <= tolerance(this.#nextThreshold, current.travelledDistance)
          ? current.travelledDistance
          : this.#nextThreshold;
        const fraction = projected ? 1 : Math.max(0, Math.min(1,
          (thresholdDistance - previous.travelledDistance) / distanceDelta,
        ));
        events.push(Object.freeze({
          position: Object.freeze({
            x: previous.x + dx * fraction,
            z: previous.z + dz * fraction,
          }),
          radius: this.#soundRadius,
          thresholdDistance,
        }));
        this.#nextThreshold += this.#strideDistance;
      }
    }

    this.#lastDistance = current.travelledDistance;
    return Object.freeze(events);
  }
}
